import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider, useToast } from './components/Toast';
import Header from './components/Header';
import LoginPage from './pages/LoginPage';
import SchedulePage from './pages/SchedulePage';
import PlanningTestPage from './pages/PlanningTestPage';
import KpiPage from './pages/KpiPage';
import ChatPage, { connectSocket, disconnectSocket, onNewMessage, onNewsPublished } from './pages/ChatPage';
import DluoPage from './pages/DluoPage';
import AdminFiguresPage from './pages/AdminFiguresPage';
import { UserManagementModal, StoreManagementModal } from './components/AdminPanels';
import { NewsComposerModal, NewsPopup } from './components/News';
import { api } from './utils/api';
import { requestNotificationPermission, subscribePush } from './utils/push';
import { isModernPlanningStore } from './utils/planningStores';
import { isModernKpiStore } from './utils/kpiModernAccess';

import './styles/variables.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/schedule.css';
import './styles/planning-test.css';
import './styles/kpi.css';
import './styles/kpi-test.css';
import './styles/chat.css';
import './styles/news.css';

const MOIS = ['','Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function PlaceholderPage({ title, icon }) {
  return (
    <div className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '4rem', marginBottom: 16 }}>{icon}</div>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{title}</h2>
        <p style={{ fontSize: '0.9rem' }}>Cette fonctionnalité sera bientôt disponible.</p>
        <div style={{ marginTop: 20, padding: '12px 20px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', display: 'inline-block', fontSize: '0.8rem' }}>
          🚧 En cours de développement
        </div>
      </div>
    </div>
  );
}

// ========== Sessions Modal ==========
function SessionsModal({ isOpen, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api.getSessions().then(setSessions).catch(() => {}).finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const ROLE_LABELS = { admin: '⚡ Admin', responsable: '🔑 Responsable', user: '👤 Utilisateur', both: '⚡ Admin+User' };

  const formatDT = (dt) => {
    if (!dt) return null;
    const d = new Date(dt + 'Z');
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
        <div className="modal-header"><h2>🔌 Historique des connexions</h2><button className="modal-close" onClick={onClose}>&times;</button></div>
        <div style={{ padding: 20 }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 16 }}>15 derniers jours</p>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }} className="loading-pulse">Chargement...</div>
          ) : sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucune connexion enregistrée</div>
          ) : (
            <div style={{ maxHeight: 450, overflowY: 'auto' }}>
              <table className="kpi-obj-table" style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Rôle</th>
                    <th>Connexion</th>
                    <th>Déconnexion</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                          <strong>{s.username}</strong>
                        </div>
                      </td>
                      <td><span style={{ fontSize: '0.72rem' }}>{ROLE_LABELS[s.role] || s.role}</span></td>
                      <td>{formatDT(s.login_at)}</td>
                      <td>
                        {s.logout_at ? (
                          formatDT(s.logout_at)
                        ) : s.is_online ? (
                          <span style={{ color: 'var(--accent-green)', fontWeight: 600, fontSize: '0.75rem' }}>🟢 Connecté</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>⚪ Inactif</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== Missing Import Warning ==========
function MissingImportWarning({ missingImports, onDismiss }) {
  if (!missingImports || missingImports.length === 0) return null;

  return (
    <div className="modal-overlay" onClick={onDismiss}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header" style={{ background: 'rgba(239,68,68,0.08)' }}>
          <h2>⚠️ Import manquant</h2>
          <button className="modal-close" onClick={onDismiss}>&times;</button>
        </div>
        <div style={{ padding: 24 }}>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: 16, lineHeight: 1.6 }}>
            Aucune donnée n'a été importée pour le mois précédent sur les magasins suivants :
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {missingImports.map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 'var(--radius-sm)' }}>
                <span style={{ fontSize: '1.2rem' }}>🏪</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.store_name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{MOIS[m.month]} {m.year} — aucune donnée</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
            Pensez à importer le fichier Excel (.xlsx) via la page <strong>📊 Chiffres → ⚙️ Menu → 📥 Importer un mois</strong>.
          </p>
          <div style={{ textAlign: 'right' }}>
            <button className="btn btn-primary" onClick={onDismiss}>Compris</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== Tasks Modal ==========
function TasksModal({ isOpen, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPrio, setNewPrio] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editId, setEditId] = useState(null);
  const [editPrio, setEditPrio] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const loadTasks = () => {
    setLoading(true);
    api.getTasks().then(setTasks).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (isOpen) loadTasks(); }, [isOpen]);

  const handleCreate = async () => {
    if (!newDesc.trim()) return;
    try {
      await api.createTask({ priority: parseInt(newPrio) || (tasks.length + 1), description: newDesc });
      setNewPrio(''); setNewDesc('');
      loadTasks();
    } catch (e) {}
  };

  const handleUpdate = async (id) => {
    try {
      await api.updateTask(id, { priority: parseInt(editPrio), description: editDesc });
      setEditId(null);
      loadTasks();
    } catch (e) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try { await api.deleteTask(id); loadTasks(); } catch (e) {}
  };

  const startEdit = (t) => { setEditId(t.id); setEditPrio(String(t.priority)); setEditDesc(t.description); };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 750 }}>
        <div className="modal-header"><h2>📝 Tâches</h2><button className="modal-close" onClick={onClose}>&times;</button></div>
        <div style={{ padding: 20 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }} className="loading-pulse">Chargement...</div>
          ) : (
            <>
              <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 16 }}>
                <table className="kpi-obj-table" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60, textAlign: 'center' }}>Priorité</th>
                      <th>Description</th>
                      <th style={{ width: 70, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.length === 0 && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Aucune tâche</td></tr>
                    )}
                    {tasks.map(t => (
                      <tr key={t.id}>
                        <td style={{ textAlign: 'center' }}>
                          {editId === t.id ? (
                            <input className="form-input" type="number" min="1" value={editPrio} onChange={e => setEditPrio(e.target.value)} style={{ width: 50, textAlign: 'center', fontSize: '0.8rem', padding: '3px 4px' }} />
                          ) : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-blue)', color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>{t.priority}</span>
                          )}
                        </td>
                        <td>
                          {editId === t.id ? (
                            <textarea className="form-input" value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2} style={{ fontSize: '0.8rem', resize: 'vertical', width: '100%' }} />
                          ) : (
                            <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{t.description}</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {editId === t.id ? (
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              <button onClick={() => handleUpdate(t.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1rem' }} title="Valider">✅</button>
                              <button onClick={() => setEditId(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1rem' }} title="Annuler">❌</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              <button onClick={() => startEdit(t)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem' }} title="Modifier">✏️</button>
                              <button onClick={() => handleDelete(t.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem' }} title="Supprimer">🗑️</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* New task form */}
              <div style={{ padding: '14px 16px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>➕ Nouvelle tâche</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <input className="form-input" type="number" min="1" placeholder="#" value={newPrio} onChange={e => setNewPrio(e.target.value)} style={{ width: 55, textAlign: 'center', fontSize: '0.82rem' }} />
                  <textarea className="form-input" placeholder="Description de la tâche..." value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={2} style={{ flex: 1, fontSize: '0.82rem', resize: 'vertical' }} />
                  <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={!newDesc.trim()} style={{ alignSelf: 'flex-end' }}>Ajouter</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== Main App ==========
function AppContent() {
  const { user, loading, isAdmin, isResponsable, canView } = useAuth();
  const [currentPage, setCurrentPage] = useState('planning');
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [showStoreMgmt, setShowStoreMgmt] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [showNewsComposer, setShowNewsComposer] = useState(false);
  const [pendingNews, setPendingNews] = useState([]);
  const [stores, setStores] = useState([]);
  const [missingImports, setMissingImports] = useState(null);
  const [unreadChat, setUnreadChat] = useState(0);
  const [planningStore, setPlanningStore] = useState(() => {
    try { return JSON.parse(localStorage.getItem('currentPlanningStore') || 'null'); }
    catch (error) { return null; }
  });
  const [kpiStore, setKpiStore] = useState(() => {
    try { return JSON.parse(localStorage.getItem('currentKpiStore') || 'null'); }
    catch (error) { return null; }
  });
  const toast = useToast();

  const isPhilippe = String(user?.username || '').trim().toLowerCase() === 'philippe';
  const isLaure = String(user?.username || '').trim().toLowerCase() === 'laure';
  const canOpenPlanningTest = isPhilippe;
  const modernPlanningStore = isModernPlanningStore(planningStore);

  const handlePlanningStoreChange = useCallback((store) => {
    const compactStore = store ? { id: store.id, name: store.name, color: store.color } : null;
    setPlanningStore(compactStore);
    if (compactStore) localStorage.setItem('currentPlanningStore', JSON.stringify(compactStore));
    else localStorage.removeItem('currentPlanningStore');
  }, []);

  const handleKpiStoreChange = useCallback((store) => {
    const compactStore = store ? { id: store.id, name: store.name, color: store.color } : null;
    setKpiStore(compactStore);
    if (compactStore) localStorage.setItem('currentKpiStore', JSON.stringify(compactStore));
    else localStorage.removeItem('currentKpiStore');
  }, []);

  useEffect(() => {
    const modernKpiStore = currentPage === 'chiffres' && isModernKpiStore(kpiStore);
    document.body.classList.toggle('planning-test-active', Boolean(user && (currentPage === 'planning-test' || (currentPage === 'planning' && modernPlanningStore) || modernKpiStore)));
    return () => document.body.classList.remove('planning-test-active');
  }, [user, currentPage, modernPlanningStore, kpiStore]);

  // Poll unread count
  const loadUnread = useCallback(() => {
    if (!user) return;
    api.chatUnread().then(d => setUnreadChat(d.unread || 0)).catch(() => {});
  }, [user]);

  useEffect(() => { loadUnread(); }, [loadUnread]);

  useEffect(() => {
    if (!user || currentPage === 'messages') return;
    if (currentPage === 'admin-figures') {
      if (isAdmin || isLaure) return;
      const fallback = ['planning', 'chiffres', 'dluo', 'stocks'].find(page => canView(page)) || 'messages';
      setCurrentPage(fallback);
      localStorage.setItem('currentPage', fallback);
      return;
    }
    if (currentPage === 'planning-test') {
      if (canOpenPlanningTest) return;
      setCurrentPage('planning');
      localStorage.setItem('currentPage', 'planning');
      return;
    }
    if (currentPage === 'chiffres-test') {
      setCurrentPage('chiffres');
      localStorage.setItem('currentPage', 'chiffres');
      return;
    }
    const requiredPermission = { planning: 'planning', chiffres: 'chiffres', dluo: 'dluo', stocks: 'stocks' }[currentPage];
    if (!requiredPermission || canView(requiredPermission)) return;
    const fallback = ['planning', 'chiffres', 'dluo', 'stocks'].find(page => canView(page)) || 'messages';
    setCurrentPage(fallback);
    localStorage.setItem('currentPage', fallback);
  }, [user, currentPage, canView, canOpenPlanningTest, isAdmin, isLaure]);

  // Keep one socket for the authenticated user. Page navigation must not
  // disconnect it or alter the server-side presence state.
  useEffect(() => {
    if (!user) return;
    connectSocket();
    return () => disconnectSocket();
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const addNews = news => {
      if (!active) return;
      setPendingNews(current => current.some(item => item.id === news.id) ? current : [...current, news]);
    };
    api.getPendingNews().then(items => {
      if (!active) return;
      setPendingNews(current => {
        const merged = [...current];
        for (const item of items) if (!merged.some(existing => existing.id === item.id)) merged.push(item);
        return merged.sort((a, b) => a.id - b.id);
      });
    }).catch(() => {});
    const unsubscribe = onNewsPublished(addNews);
    return () => { active = false; unsubscribe(); setPendingNews([]); };
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const unsub = onNewMessage((msg) => {
      // Update badge
      loadUnread();
      // Toast + browser notification when not on chat page
      if (currentPage !== 'messages' && msg.user_id !== user.id) {
        toast(`💬 ${msg.username}: ${msg.content || '📎 Fichier'}`, 'info');
        // Browser notification (desktop)
        if (Notification.permission === 'granted') {
          new Notification(`💬 ${msg.username}`, {
            body: msg.content || '📎 Fichier envoyé',
            icon: '/icon-192x192.png',
            tag: `chat_${msg.room_id}`,
          });
        }
      }
    });

    return unsub;
  }, [user, currentPage, loadUnread, toast]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(loadUnread, 30000);
    return () => clearInterval(interval);
  }, [user, loadUnread]);

  useEffect(() => {
    if (user && isAdmin) {
      api.getStores().then(setStores).catch(() => {});
    }
  }, [user, isAdmin]);

  // Auto-subscribe to push notifications
  useEffect(() => {
    if (!user) return;
    const initPush = async () => {
      const perm = await requestNotificationPermission();
      if (perm === 'granted') await subscribePush();
    };
    initPush();
  }, [user]);

  // Check for missing imports on first day of month for admin/responsable
  useEffect(() => {
    if (!user) return;
    if (!isAdmin && !isResponsable) return;

    // Only show once per session
    const dismissKey = `import_warning_dismissed_${new Date().getFullYear()}_${new Date().getMonth() + 1}`;
    if (sessionStorage.getItem(dismissKey)) return;

    api.getMissingImports().then(data => {
      if (data && data.length > 0) {
        setMissingImports(data);
      }
    }).catch(() => {});
  }, [user, isAdmin, isResponsable]);

  const dismissImportWarning = () => {
    const dismissKey = `import_warning_dismissed_${new Date().getFullYear()}_${new Date().getMonth() + 1}`;
    sessionStorage.setItem(dismissKey, '1');
    setMissingImports(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem('currentPage', page);
  };

  const handleAdminAction = (action) => {
    if (action === 'users') setShowUserMgmt(true);
    if (action === 'stores') setShowStoreMgmt(true);
    if (action === 'figures') handlePageChange('admin-figures');
    if (action === 'sessions') setShowSessions(true);
    if (action === 'tasks') setShowTasks(true);
    if (action === 'news') setShowNewsComposer(true);
  };

  const closeCurrentNews = async () => {
    const current = pendingNews[0];
    if (!current) return;
    try {
      await api.closeNews(current.id);
      setPendingNews(items => items.filter(item => item.id !== current.id));
    } catch (error) {
      toast(error.message, 'error');
    }
  };

  const refreshStores = () => { api.getStores().then(setStores).catch(() => {}); };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: 'var(--text-muted)' }}>
        <div className="loading-pulse" style={{ fontSize: '1.1rem' }}>Chargement...</div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const headerStore = currentPage === 'chiffres' ? kpiStore : planningStore;

  return (
    <>
      <Header currentPage={currentPage} onPageChange={handlePageChange} onAdminAction={handleAdminAction} unreadChat={unreadChat} currentStore={headerStore} />
      {currentPage === 'planning' && canView('planning') && <SchedulePage onStoreChange={handlePlanningStoreChange} onOpenPlanningHelp={() => handlePageChange('admin-figures')} />}
      {currentPage === 'planning-test' && canOpenPlanningTest && <PlanningTestPage onStoreResolved={handlePlanningStoreChange} />}
      {currentPage === 'chiffres' && canView('chiffres') && <KpiPage onStoreChange={handleKpiStoreChange} />}
      {currentPage === 'dluo' && canView('dluo') && <DluoPage />}
      {currentPage === 'stocks' && canView('stocks') && <PlaceholderPage title="Gestion Stocks" icon="🏷️" />}
      {currentPage === 'messages' && <ChatPage />}
      {currentPage === 'admin-figures' && (isAdmin || isLaure) && <AdminFiguresPage restricted={!isAdmin} lockedStoreId={planningStore?.id} />}

      {/* Global admin modals */}
      {isAdmin && (
        <>
          <UserManagementModal isOpen={showUserMgmt} onClose={() => setShowUserMgmt(false)} stores={stores} onRefresh={refreshStores} />
          <StoreManagementModal isOpen={showStoreMgmt} onClose={() => setShowStoreMgmt(false)} onRefresh={refreshStores} />
          <SessionsModal isOpen={showSessions} onClose={() => setShowSessions(false)} />
          <TasksModal isOpen={showTasks} onClose={() => setShowTasks(false)} />
        </>
      )}

      {(isAdmin || isPhilippe) && <NewsComposerModal isOpen={showNewsComposer} onClose={() => setShowNewsComposer(false)} />}

      {/* Missing import warning */}
      <MissingImportWarning missingImports={missingImports} onDismiss={dismissImportWarning} />
      <NewsPopup news={pendingNews[0]} onClose={closeCurrentNews} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}
